import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Spin,
  message,
  Tooltip,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import { fetchJournalEntries, getFirstPageJournalEntries } from "../../api/accounting";
import CreateJournalEntryModal from "./CreateJournalEntryModal";
import {
  formatCurrencyWithConversion,
  convertToUSD,
  formatInDisplayCurrency,
  getGlobalDisplayCurrency,
} from "../../utils/currencyUtils";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  getCachedData,
  setCachedData,
  CacheTTL,
} from "../../context/AccountingDataCache";

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * Journal Entries Component
 *
 * This displays ALL journal entries (automatic + manual) for the selected brand.
 *
 * AUTOMATIC Journal Entries are created by the system when:
 * - Invoices/Bills are created (post_to_ledger)
 * - Payments are allocated (PaymentAllocation.save)
 * - Credits are allocated (CreditAllocation.save)
 * - Overpayments are created/allocated
 *
 * MANUAL Journal Entries are created by accountants for:
 * - Adjustments, corrections
 * - Opening balances
 * - Transactions not captured elsewhere
 *
 * Uses /api/journal/ endpoint which returns ALL journal entries (automatic + manual)
 */
const JournalEntries = ({ selectedBrand, selectedBrands = [] }) => {
  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const {
    refreshKey: currencyRefreshKey,
    displayCurrency,
    exchangeRate,
    isConverted,
  } = useDisplayCurrency();

  const [filters, setFilters] = useState({
    search: "",
    type: "",
    dateRange: [dayjs().subtract(30, "days"), dayjs()],
    account: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const gridApiRef = useRef(null);

  // Filter and sort persistence refs
  const savedFilterStateRef = useRef(null);
  const savedSortStateRef = useRef([{ colId: "date", sort: "desc" }]);
  const pendingFilterRestoreRef = useRef(false);
  const pendingSortRestoreRef = useRef(false);

  // Determine which brands to use
  const brandsToUse = useMemo(() => {
    if (selectedBrands && selectedBrands.length > 0) {
      return selectedBrands;
    }
    return selectedBrand ? [selectedBrand] : [];
  }, [selectedBrands, selectedBrand]);

  // Create stable key for global cache
  const brandsKey = useMemo(() => {
    if (!brandsToUse || brandsToUse.length === 0) return "";
    return brandsToUse
      .map((b) => b.id)
      .sort()
      .join(",");
  }, [brandsToUse]);

  const cacheKey = useMemo(() => {
    if (!brandsKey) return null;
    const dateKey =
      filters.dateRange && filters.dateRange.length === 2
        ? `${filters.dateRange[0].format(
            "YYYY-MM-DD"
          )}__${filters.dateRange[1].format("YYYY-MM-DD")}`
        : "no-date";
    return `journal_entries_${brandsKey}_${filters.type || "all"}_${dateKey}`;
  }, [brandsKey, filters.dateRange, filters.type]);

  // Check global cache for initial data - instant restore on navigation
  const initialCachedData = useMemo(() => {
    if (!cacheKey) return null;
    return getCachedData(cacheKey);
  }, [cacheKey]);

  // Initialize state from cache if available
  const [journalEntries, setJournalEntries] = useState(
    () => initialCachedData || []
  );
  const [loading, setLoading] = useState(false);
  
  // Background loading state for hybrid loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const backgroundLoadingRef = useRef(false);
  const backgroundAbortRef = useRef(false);

  // Fetch Journal Entries
  const deriveEntryKey = useCallback((entry) => {
    if (!entry) return `JE-${entry?.id || ""}`;

    // Group by allocation first (payment, credit, cash, overpayment allocations)
    if (entry.allocation) {
      return `JE-${entry.allocation}`;
    }

    if (entry.credit_allocation) {
      return `JE-${entry.credit_allocation}`;
    }

    if (entry.cash_allocation) {
      return `JE-${entry.cash_allocation}`;
    }

    if (entry.overpayment_allocation) {
      return `JE-${entry.overpayment_allocation}`;
    }

    // Group overpayment creation lines by the overpayment id
    if (entry.partner_overpayment) {
      return `JE-OP-${entry.partner_overpayment}`;
    }

    // Group manual journal entries by batch
    if (entry.manual_batch_id) {
      return `JE-${entry.manual_batch_id}`;
    }

    // Group invoice journal entries by invoice ID (all entries for same invoice should be grouped)
    if (entry.invoice) {
      return `JE-INV-${entry.invoice}`;
    }

    // Group credit note journal entries by credit ID
    if (entry.credit_note) {
      return `JE-CREDIT-${entry.credit_note}`;
    }

    // Fallback to entry ID for any other entries
    return `JE-${entry.id}`;
  }, []);

  const fetchInFlightRef = useRef(false);
  const pendingFetchKeyRef = useRef(null);
  const lastCompletedFetchKeyRef = useRef(initialCachedData ? cacheKey : null);
  const fetchDataRef = useRef(null);

  // Helper function to process and sort entries
  const processEntries = useCallback((data) => {
    // Group entries by JE number first
    const groupedByJE = {};
    data.forEach((entry) => {
      const jeKey = deriveEntryKey(entry);
      if (!groupedByJE[jeKey]) {
        groupedByJE[jeKey] = [];
      }
      groupedByJE[jeKey].push(entry);
    });

    // Sort each group by date descending, then flatten
    const sortedGroups = Object.entries(groupedByJE)
      .map(([jeKey, entries]) => {
        // Sort entries within same JE by date descending (newest first)
        const sortedEntries = [...entries].sort((a, b) => {
          const dateA = a.date
            ? dayjs(a.date)
            : dayjs(a.timestamp || a.created_at || 0);
          const dateB = b.date
            ? dayjs(b.date)
            : dayjs(b.timestamp || b.created_at || 0);
          return dateB.valueOf() - dateA.valueOf(); // Descending
        });
        return {
          jeKey,
          entries: sortedEntries,
          maxDate:
            sortedEntries[0]?.date ||
            sortedEntries[0]?.timestamp ||
            sortedEntries[0]?.created_at ||
            0,
        };
      })
      .sort((a, b) => {
        // Sort groups by max date descending (newest JE first)
        const dateA = dayjs(a.maxDate);
        const dateB = dayjs(b.maxDate);
        if (!dateA.isSame(dateB, "day")) {
          return dateB.valueOf() - dateA.valueOf(); // Descending
        }
        // If same date, sort by JE number descending
        const numA = parseInt(a.jeKey.replace(/\D+/g, ""), 10) || 0;
        const numB = parseInt(b.jeKey.replace(/\D+/g, ""), 10) || 0;
        return numB - numA; // Descending
      });

    // Flatten back to array, maintaining the grouped order
    return sortedGroups.flatMap((group) => group.entries);
  }, [deriveEntryKey]);

  const fetchData = useCallback(
    async ({ force = false } = {}) => {
      if (!brandsToUse || brandsToUse.length === 0 || !cacheKey) {
        setJournalEntries([]);
        setLoading(false);
        return;
      }

      const sortedBrandIds = [...brandsToUse.map((b) => b.id)].sort(
        (a, b) => a - b
      );
      const fetchKey = cacheKey;

      // Abort any ongoing background loading
      backgroundAbortRef.current = true;

      // Check GLOBAL cache first if not forcing refresh - prevents refetch on navigation
      if (!force) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          setJournalEntries(cachedData);
          lastCompletedFetchKeyRef.current = fetchKey;
          return;
        }
      }

      if (!force) {
        if (fetchInFlightRef.current) {
          if (pendingFetchKeyRef.current !== fetchKey) {
            pendingFetchKeyRef.current = fetchKey;
          }
          return;
        }

        if (lastCompletedFetchKeyRef.current === fetchKey) {
          return;
        }
      }

      fetchInFlightRef.current = true;
      setLoading(true);

      try {
        const params = {
          brandIds: sortedBrandIds,
        };

        if (filters.dateRange && filters.dateRange.length === 2) {
          params.start = filters.dateRange[0].format("YYYY-MM-DD");
          params.end = filters.dateRange[1].format("YYYY-MM-DD");
        }

        if (filters.search) {
          params.search = filters.search;
        }

        if (filters.type) {
          params.type = filters.type;
        }

        if (filters.account) {
          params.account = filters.account;
        }

        // ========== PHASE 1: Quick first page load (1000 items) ==========
        const firstPageResult = await getFirstPageJournalEntries(params);
        const firstPageData = firstPageResult.results || [];

        // Process and display first page immediately
        const processedFirstPage = processEntries(firstPageData);
        setJournalEntries(processedFirstPage);
        setLoading(false);

        // ========== PHASE 2: Load remaining data in background ==========
        if (firstPageResult.hasMore) {
          backgroundAbortRef.current = false;
          backgroundLoadingRef.current = true;
          setIsLoadingMore(true);

          // Small delay to let UI render first
          setTimeout(async () => {
            try {
              const response = await fetchJournalEntries(params);
              
              // Check if we should abort
              if (backgroundAbortRef.current) {
                backgroundLoadingRef.current = false;
                setIsLoadingMore(false);
                return;
              }

              const data = response?.results || response?.data || response || [];
              const sortedData = processEntries(data);

              setJournalEntries(sortedData);
              lastCompletedFetchKeyRef.current = fetchKey;

              // Cache the full results in GLOBAL cache - persists across page navigation
              setCachedData(cacheKey, sortedData, CacheTTL.MEDIUM);
            } catch (bgError) {
              console.error("Background loading error:", bgError);
            } finally {
              backgroundLoadingRef.current = false;
              setIsLoadingMore(false);
            }
          }, 100);
        } else {
          // No more data to load - cache the first page results
          lastCompletedFetchKeyRef.current = fetchKey;
          setCachedData(cacheKey, processedFirstPage, CacheTTL.MEDIUM);
        }
      } catch (error) {
        message.error(`Failed to fetch journal entries: ${error.message}`);
        setLoading(false);
      } finally {
        fetchInFlightRef.current = false;

        if (
          pendingFetchKeyRef.current &&
          pendingFetchKeyRef.current !== lastCompletedFetchKeyRef.current
        ) {
          pendingFetchKeyRef.current = null;
          lastCompletedFetchKeyRef.current = null;
          fetchData({ force: true });
        } else {
          pendingFetchKeyRef.current = null;
        }
      }
    },
    [brandsToUse, filters, cacheKey, processEntries]
  );

  // Keep ref updated with latest fetchData
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Fetch data when brands or filters change - with debounce
  useEffect(() => {
    if (!brandsToUse || brandsToUse.length === 0) {
      setJournalEntries([]);
      return;
    }

    // Skip if we already have cached data for this key
    if (
      journalEntries.length > 0 &&
      lastCompletedFetchKeyRef.current === cacheKey
    ) {
      return;
    }

    // Small delay to batch multiple rapid changes
    const timeoutId = setTimeout(() => {
      if (!fetchInFlightRef.current && fetchDataRef.current) {
        fetchDataRef.current();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [brandsToUse, filters, cacheKey, journalEntries.length]);

  // Effect to restore filters and sort AFTER data has loaded
  useEffect(() => {
    const hasFiltersToRestore =
      pendingFilterRestoreRef.current &&
      savedFilterStateRef.current &&
      Object.keys(savedFilterStateRef.current).length > 0;
    const hasSortToRestore =
      pendingSortRestoreRef.current &&
      savedSortStateRef.current &&
      savedSortStateRef.current.length > 0;

    if (
      (hasFiltersToRestore || hasSortToRestore) &&
      !loading &&
      journalEntries.length > 0
    ) {
      const restoreGridState = (attempt = 1) => {
        setTimeout(() => {
          if (gridApiRef.current) {
            try {
              if (hasFiltersToRestore) {
                gridApiRef.current.setFilterModel(savedFilterStateRef.current);
                pendingFilterRestoreRef.current = false;
              }
              if (hasSortToRestore) {
                gridApiRef.current.applyColumnState({
                  state: savedSortStateRef.current,
                  defaultState: { sort: null },
                });
                pendingSortRestoreRef.current = false;
              }
            } catch (e) {
              if (attempt < 5) restoreGridState(attempt + 1);
              else {
                pendingFilterRestoreRef.current = false;
                pendingSortRestoreRef.current = false;
              }
            }
          } else if (attempt < 5) {
            restoreGridState(attempt + 1);
          } else {
            pendingFilterRestoreRef.current = false;
            pendingSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreGridState();
    }
  }, [loading, journalEntries.length]);

  // Filter handlers
  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleTypeChange = (value) => {
    setFilters((prev) => ({ ...prev, type: value }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({ ...prev, dateRange: dates }));
  };

  const handleSearch = () => {
    fetchData({ force: true });
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      type: "",
      dateRange: [dayjs().subtract(30, "days"), dayjs()],
      account: "",
    });
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setSelectedEntry(null);
    setIsEditMode(false);
    fetchData({ force: true });
  };

  const handleCreateEntry = () => {
    setSelectedEntry(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleViewEntry = (entry) => {
    setSelectedEntry(entry);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const filteredEntries = useMemo(() => {
    let entries = [...journalEntries];

    if (filters.type) {
      entries = entries.filter(
        (entry) => String(entry.entry_type).toLowerCase() === filters.type
      );
    }

    if (filters.search) {
      const searchTerm = filters.search.trim().toLowerCase();
      if (searchTerm) {
        entries = entries.filter((entry) => {
          const fieldsToSearch = [
            entry.description,
            entry.account_name,
            entry.account_code,
            entry.partner_name,
            entry.invoice_number,
            entry.credit_number,
            entry.manual_reference,
            entry.manual_batch_reference,
          ];
          return fieldsToSearch.some((field) =>
            String(field || "")
              .toLowerCase()
              .includes(searchTerm)
          );
        });
      }
    }

    if (filters.account) {
      entries = entries.filter(
        (entry) =>
          String(entry.account) === String(filters.account) ||
          String(entry.account_name || "")
            .toLowerCase()
            .includes(String(filters.account || "").toLowerCase())
      );
    }

    return entries;
  }, [journalEntries, filters]);

  // Display journal entries with entry_number field for grouping
  // Entries are already sorted and grouped from fetchData, just add metadata
  const groupedEntries = useMemo(() => {
    return filteredEntries.map((entry) => ({
      ...entry,
      entry_number: deriveEntryKey(entry), // Add entry_number field for grouping
      isGroup: false,
      accounts: entry.account_name,
      firstEntry: entry,
    }));
  }, [filteredEntries, deriveEntryKey]);

  // Calculate totals for display (convert to USD)
  const calculateTotals = useMemo(() => {
    const totalDebit = filteredEntries.reduce((sum, entry) => {
      const currency = entry.currency || "$";
      const converted = convertToUSD(entry.debit || 0, currency);
      return sum + converted;
    }, 0);
    const totalCredit = filteredEntries.reduce((sum, entry) => {
      const currency = entry.currency || "$";
      const converted = convertToUSD(entry.credit || 0, currency);
      return sum + converted;
    }, 0);
    return { totalDebit, totalCredit };
  }, [filteredEntries]);

  // AG Grid column definitions with enhanced actions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Entry #",
        field: "entry_number",
        colId: "entry_number",
        width: 120,
        rowGroup: true,
        hide: true,
        valueGetter: (params) => deriveEntryKey(params.data),
      },
      {
        headerName: "JE #",
        field: "je_number",
        colId: "je_number",
        width: 120,
        pinned: "left",
        sortable: true,
        filter: true,
        valueGetter: (params) => deriveEntryKey(params.data),
        cellStyle: { fontWeight: 600 },
      },
      {
        headerName: "Date",
        field: "date",
        width: 120,
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            if (cellDate < filterLocalDateAtMidnight) return -1;
            if (cellDate > filterLocalDateAtMidnight) return 1;
            return 0;
          },
        },
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          return new Date(params.value).toLocaleDateString();
        },
      },
      {
        headerName: "Account",
        field: "account_name",
        width: 250,
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "equals", "startsWith", "endsWith"],
          defaultOption: "contains",
          trimInput: true,
        },
      },
      {
        headerName: "Reference",
        field: "reference",
        width: 180,
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "equals", "startsWith", "endsWith"],
          defaultOption: "contains",
          trimInput: true,
        },
        valueGetter: (params) => {
          const data = params.data;
          if (data.invoice_number) return `Invoice #${data.invoice_number}`;
          if (data.credit_number) return `Credit #${data.credit_number}`;
          if (data.manual_batch_reference) return data.manual_batch_reference;
          if (data.manual_reference) return data.manual_reference;
          return "";
        },
      },
      {
        headerName: "Description",
        field: "description",
        width: 300,
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: [
            "contains",
            "equals",
            "startsWith",
            "endsWith",
            "notContains",
          ],
          defaultOption: "contains",
          trimInput: true,
          caseSensitive: false,
        },
        valueGetter: (params) => {
          // Ensure description is always a string for filtering
          return params.data.description || "";
        },
      },
      {
        headerName: "Debit",
        field: "debit",
        width: 130,
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          allowedCharPattern: "\\d\\-\\,\\.", // Allow numbers, decimals, negative signs
          numberParser: (text) => {
            return text == null ? null : parseFloat(text.replace(/,/g, ""));
          },
        },
        cellRenderer: (params) => {
          if (params.value == null || params.value === 0) return "";
          const currency = params.data.currency || "$";
          return formatCurrencyWithConversion(params.value, currency);
        },
        // Professional standard: neutral color for debits (entries are neither good nor bad)
        cellStyle: { textAlign: "right", fontWeight: "500" },
      },
      {
        headerName: "Credit",
        field: "credit",
        width: 130,
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          allowedCharPattern: "\\d\\-\\,\\.", // Allow numbers, decimals, negative signs
          numberParser: (text) => {
            return text == null ? null : parseFloat(text.replace(/,/g, ""));
          },
        },
        cellRenderer: (params) => {
          if (params.value == null || params.value === 0) return "";
          const currency = params.data.currency || "$";
          return formatCurrencyWithConversion(params.value, currency);
        },
        // Professional standard: neutral color for credits (entries are neither good nor bad)
        cellStyle: { textAlign: "right", fontWeight: "500" },
      },
      {
        headerName: "Actions",
        field: "__actions",
        colId: "actionsColumn",
        width: 120,
        pinned: "right",
        lockPosition: true,
        lockPinned: true,
        cellRenderer: (params) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewEntry(params.data.firstEntry)}
              />
            </Tooltip>
            <Tooltip title="Edit Entry">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditEntry(params.data.firstEntry)}
                disabled={params.data.is_reversal}
              />
            </Tooltip>
          </Space>
        ),
      },
    ];
  }, [deriveEntryKey]);

  // Auto group column definition for Entry # display
  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Entry #",
      minWidth: 180,
      pinned: "left",
      lockPinned: true,
      valueGetter: (params) => {
        // For group rows, get the entry_number from the first child
        if (params.node.group) {
          const children = params.node.allChildren;
          if (children && children.length > 0) {
            return deriveEntryKey(children[0].data);
          }
        }
        // For leaf rows, get from data
        return params.data?.entry_number || deriveEntryKey(params.data);
      },
      cellRendererParams: {
        suppressCount: true,
      },
    }),
    [deriveEntryKey]
  );

  // Default column definition
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  // Show message if no brand is selected
  if (!selectedBrand || !selectedBrand.id) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h3>Please select a brand to view journal entries</h3>
        <p style={{ color: "#666" }}>
          Select a brand from the dropdown above to load journal entries.
        </p>
        <Button
          type="primary"
          onClick={async () => {
            try {
              const response = await fetchJournalEntries({});
              message.success(
                `Fetched ${
                  response?.results?.length ?? 0
                } entries (no brand filter)`
              );
            } catch (error) {
              message.error("Test request failed");
            }
          }}
        >
          Test API Call (No Brand Filter)
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#52c41a",
                }}
              >
                {isConverted && (
                  <Tooltip
                    title={`Original: $${calculateTotals.totalDebit.toLocaleString(
                      "en-US",
                      { minimumFractionDigits: 2 }
                    )} USD | Rate: 1 USD = ${exchangeRate} ${displayCurrency}`}
                  >
                    <SwapOutlined style={{ fontSize: 14, marginRight: 6 }} />
                  </Tooltip>
                )}
                {isConverted
                  ? `${(
                      calculateTotals.totalDebit * exchangeRate
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })} ${displayCurrency}`
                  : formatInDisplayCurrency(calculateTotals.totalDebit)}
              </div>
              <div style={{ color: "#666" }}>Total Debits</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#ff4d4f",
                }}
              >
                {isConverted && (
                  <Tooltip
                    title={`Original: $${calculateTotals.totalCredit.toLocaleString(
                      "en-US",
                      { minimumFractionDigits: 2 }
                    )} USD | Rate: 1 USD = ${exchangeRate} ${displayCurrency}`}
                  >
                    <SwapOutlined style={{ fontSize: 14, marginRight: 6 }} />
                  </Tooltip>
                )}
                {isConverted
                  ? `${(
                      calculateTotals.totalCredit * exchangeRate
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })} ${displayCurrency}`
                  : formatInDisplayCurrency(calculateTotals.totalCredit)}
              </div>
              <div style={{ color: "#666" }}>Total Credits</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1890ff",
                }}
              >
                {groupedEntries.length}
              </div>
              <div style={{ color: "#666" }}>Total Transactions</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Input
              placeholder="Search description or account"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={handleSearchChange}
              style={{ width: "100%" }}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Entry Type"
              prefix={<FilterOutlined />}
              style={{ width: "100%" }}
              value={filters.type}
              onChange={handleTypeChange}
              allowClear
            >
              <Option value="inv">Invoice Entry (Auto)</Option>
              <Option value="credit">Credit Note Entry (Auto)</Option>
              <Option value="overpayment">Overpayment Entry (Auto)</Option>
              <Option value="allocation">Allocation Entry (Auto)</Option>
              <Option value="manual">Manual Entry</Option>
              <Option value="reversal">Reversal Entry</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              style={{ width: "100%" }}
              value={filters.dateRange}
              onChange={handleDateRangeChange}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                Search
              </Button>
              <Button icon={<FilterOutlined />} onClick={handleClearFilters}>
                Clear
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h4 style={{ margin: 0 }}>Journal Entries</h4>
            <div style={{ color: "#666", fontSize: "12px" }}>
              Showing {filteredEntries.length} journal lines
              {filters.dateRange && filters.dateRange.length === 2 && (
                <span>
                  {" "}
                  from {filters.dateRange[0].format("MMM DD, YYYY")} to{" "}
                  {filters.dateRange[1].format("MMM DD, YYYY")}
                </span>
              )}
            </div>
          </div>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                try {
                  // Get filtered rows from AG Grid (respects all column filters)
                  let rowsToExport = [];
                  if (gridApiRef.current) {
                    gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
                      // Skip group rows, only export leaf nodes
                      if (node.data && !node.group) {
                        rowsToExport.push(node.data);
                      }
                    });
                  } else {
                    // Fallback to filteredEntries if grid API not available
                    rowsToExport = filteredEntries;
                  }

                  if (rowsToExport.length === 0) {
                    message.warning("No data to export");
                    return;
                  }

                  // Prepare data for export
                  const exportData = rowsToExport.map((entry) => ({
                    "JE #": entry.entry_number || "",
                    Date: entry.date
                      ? dayjs(entry.date).format("YYYY-MM-DD")
                      : "",
                    Account: entry.account_name || "",
                    Reference: entry.invoice_number
                      ? `Invoice #${entry.invoice_number}`
                      : entry.credit_number
                      ? `Credit #${entry.credit_number}`
                      : entry.manual_batch_reference ||
                        entry.manual_reference ||
                        "",
                    Description: entry.description || "",
                    Debit: entry.debit || 0,
                    Credit: entry.credit || 0,
                    Currency: entry.currency || "$",
                    Partner: entry.partner_name || "",
                    "Entry Type": entry.entry_type || "",
                  }));

                  // Create worksheet
                  const ws = XLSX.utils.json_to_sheet(exportData);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, "Journal Entries");

                  // Generate filename with date
                  const filename = `Journal_Entries_${dayjs().format(
                    "YYYY-MM-DD"
                  )}.xlsx`;

                  // Save file
                  XLSX.writeFile(wb, filename);
                  message.success(
                    `Exported ${rowsToExport.length} entries to ${filename}`
                  );
                } catch (error) {
                  console.error("Error exporting journal entries:", error);
                  message.error("Failed to export journal entries");
                }
              }}
            >
              Export
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchData({ force: true })}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateEntry}
            >
              Create Entry
            </Button>
          </Space>
        </div>

        <Spin spinning={loading}>
          <div
            className="ag-theme-alpine"
            style={{ height: 600, width: "100%" }}
          >
            <AgGridWithColumnState
              key={`journal-entries-${currencyRefreshKey}`}
              tableId="advance-accounting-journal-entries-v2"
              gridApiRef={gridApiRef}
              columnDefs={columnDefs}
              rowData={groupedEntries}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              groupDisplayType="groupRows"
              groupDefaultExpanded={1}
              rowSelection="multiple"
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              domLayout="normal"
              enableRangeSelection={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              animateRows={true}
              initialState={{
                sort: {
                  sortModel: [{ colId: "date", sort: "desc" }],
                },
              }}
              suppressSorting={false}
              overlayNoRowsTemplate={
                loading
                  ? '<span style="padding: 20px; color: #999;">Loading journal entries...</span>'
                  : '<span style="padding: 20px; color: #999;">No journal entries found</span>'
              }
              getRowStyle={(params) => {
                if (params.data.is_reversal) {
                  return { backgroundColor: "#fff2f0", opacity: 0.8 };
                }
                return null;
              }}
            />
          </div>
        </Spin>
      </Card>

      {/* Create Journal Entry Modal */}
      <CreateJournalEntryModal
        visible={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedEntry(null);
          setIsEditMode(false);
        }}
        onSuccess={handleModalSuccess}
        selectedBrand={selectedBrand}
        selectedEntry={selectedEntry}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default JournalEntries;
